/**
 * AI behaviour, matching the original's minimal design (MECHANICS.md §10).
 *
 * The original game has driver functions only for soldier (FUN_00410be0) and
 * cruiser (FUN_00410db0) — no production loop, no scout sweep, no defence
 * logic. Workers/trucks/scouts/guns/rocket plants/tanks owned by the AI are
 * pre-populated at game start and have no autonomous behaviour.
 *
 * We replicate that here exactly:
 *   1. Tick every AI-owned movable unit.
 *   2. If the player base hasn't been spotted, random-walk; if it has, path
 *      toward it.
 *   3. After moving, every soldier/tank/cruiser does a scan-and-shoot in its
 *      sight range. On base contact, it triggers the appropriate base-strike.
 *
 * The "base detected" flag (DAT_0049dbd0 in the decomp) is a session-scoped
 * state we own here.
 */

import {
  DIRECTION_VECTORS,
  Direction,
  type Position,
  chebyshev,
  isOnBoard,
} from "./types.js";
import type { Board } from "./grid.js";
import { type Unit, UNIT_DATA } from "./units.js";
import type { Rng } from "./rng.js";
import { type AttackResult, findTarget, applyHit, resolveBaseStrike, type BaseStrike } from "./combat.js";

/** Per-class proximity at which an AI unit "spots" the player base. */
const AI_BASE_DETECT_RANGE: Readonly<Record<number, number>> = {
  3: 4, // Soldier
  6: 5, // Tank
  8: 6, // Cruiser
} as const;

export interface AiState {
  /** True iff any AI unit has spotted the player base this game. */
  playerBaseDetected: boolean;
  /** Player base position (the AI's pathing target once detected). */
  readonly playerBase: Position;
  /** Easy mode auto-grants base detection from tick 0. */
  readonly easyModeAutoDetect: boolean;
}

export function makeAiState(playerBase: Position, easyMode: boolean): AiState {
  return {
    playerBaseDetected: easyMode,
    playerBase,
    easyModeAutoDetect: easyMode,
  };
}

/**
 * Pick a fresh direction for an AI unit. If the player base isn't detected
 * yet, random walk; otherwise pick a direction that reduces the Manhattan
 * gap (rand%3 ⇒ X-only / Y-only / diagonal, matching FUN_0040f620).
 */
function chooseDirection(unit: Unit, ai: AiState, rng: Rng): Direction {
  if (!ai.playerBaseDetected) {
    return (rng.int(8) + 1) as Direction;
  }
  const dx = ai.playerBase.x - unit.position.x;
  const dy = ai.playerBase.y - unit.position.y;
  const stepX = Math.sign(dx);
  const stepY = Math.sign(dy);
  if (stepX === 0 && stepY === 0) return unit.direction as Direction;

  const mode = rng.int(3);
  let useX: number;
  let useY: number;
  if (mode === 0) {
    useX = stepX;
    useY = 0;
  } else if (mode === 1) {
    useX = 0;
    useY = stepY;
  } else {
    useX = stepX;
    useY = stepY;
  }
  // If our preferred axis is already centred, fall back to the other axis.
  if (useX === 0 && useY === 0) {
    useX = stepX !== 0 ? stepX : 0;
    useY = stepY !== 0 ? stepY : 0;
  }
  return vectorToDirection(useX, useY) ?? (unit.direction as Direction);
}

function vectorToDirection(dx: number, dy: number): Direction | null {
  for (const [k, [vx, vy]] of Object.entries(DIRECTION_VECTORS)) {
    if (vx === dx && vy === dy) return Number(k) as Direction;
  }
  return null;
}

function stepAlong(p: Position, dir: Direction): Position {
  const v = DIRECTION_VECTORS[dir];
  return { x: p.x + v[0], y: p.y + v[1] };
}

/** Try to advance `unit` one cell in its current direction. */
function tryStep(unit: Unit, board: Board, ai: AiState, rng: Rng): void {
  if (!UNIT_DATA[unit.kind].mobile) return;

  let direction = unit.direction as Direction;
  let dest = stepAlong(unit.position, direction);
  if (!isOnBoard(dest) || !board.isEmpty(dest)) {
    direction = chooseDirection(unit, ai, rng);
    dest = stepAlong(unit.position, direction);
  }
  if (isOnBoard(dest) && board.isEmpty(dest)) {
    unit.direction = direction;
    board.moveTo(unit, dest);
  } else {
    // Blocked from both directions this tick; re-roll for next tick.
    unit.direction = chooseDirection(unit, ai, rng);
  }
}

/** Did `unit` arrive on the player base cell? */
function reachedPlayerBase(unit: Unit, ai: AiState): boolean {
  return unit.position.x === ai.playerBase.x && unit.position.y === ai.playerBase.y;
}

/** Update the "base detected" flag if `unit` is close enough to see the base. */
function maybeDetectBase(unit: Unit, ai: AiState): void {
  if (ai.playerBaseDetected) return;
  const range = AI_BASE_DETECT_RANGE[unit.kind];
  if (range === undefined) return;
  if (chebyshev(unit.position, ai.playerBase) < range) {
    ai.playerBaseDetected = true;
  }
}

/** Outcome of one AI tick — what the engine should apply / surface. */
export interface AiTickEvents {
  attacks: AttackResult[];
  baseStrike: BaseStrike | null;
}

/**
 * Drive every AI-owned movable combat unit one tick.
 * Returns the side-effect intents (attacks landed, base-strike resolution).
 */
export function tickAi(
  aiUnits: readonly Unit[],
  playerUnits: readonly Unit[],
  board: Board,
  ai: AiState,
  difficulty: number,
  rng: Rng,
): AiTickEvents {
  const events: AiTickEvents = { attacks: [], baseStrike: null };

  for (const unit of aiUnits) {
    if (!unit.alive) continue;
    const drives = unit.kind === 3 || unit.kind === 6 || unit.kind === 8;

    // Base-strike is the highest-priority action — if the unit is on the
    // player base before this tick begins (initial placement, save restore),
    // strike before moving. The original triggers the strike inside the
    // scan-and-shoot function called *after* movement; an explicit pre-check
    // here makes the behaviour total without changing the in-game outcome.
    if (drives && UNIT_DATA[unit.kind].canStrikeBase && reachedPlayerBase(unit, ai)) {
      events.baseStrike = resolveBaseStrike(unit, difficulty);
      break;
    }

    if (UNIT_DATA[unit.kind].mobile) {
      tryStep(unit, board, ai, rng);
      maybeDetectBase(unit, ai);
    }

    if (drives) {
      if (UNIT_DATA[unit.kind].canStrikeBase && reachedPlayerBase(unit, ai)) {
        events.baseStrike = resolveBaseStrike(unit, difficulty);
        break;
      }
      const target = findTarget(unit, playerUnits);
      if (target) {
        events.attacks.push(applyHit(unit, target, difficulty));
      }
    }
  }

  return events;
}
